import { Fragment } from "react";
import { DiscoverInput } from "./schema-form/DiscoverInput";
import { InfoTip } from "./primitives/InfoTip";
import { vendorLabel } from "../lib/friendly-labels";

// Record adoption, configured by an operator (spec §6.1, §6.5). Off is the
// default and today's behavior: every record the pipeline sends mints a new one
// at the destination. On, the pipeline resolves an id, writes into the record
// that is already there, and masks which properties each kind of write may
// touch.
//
// The draft below is an all-optional mirror of `DestinationIdentityPolicySchema`
// — a half-filled policy is a legitimate intermediate state on this screen, and
// the API is the validator (it returns per-field 422 issues). The browser must
// not restate those rules; the one rule enforced HERE is a safety property, not
// a validation: none of the three not-found moments may preselect `create`.

export type NotFoundChoice = "create" | "skip" | "fail";
export type WriteOperation = "create" | "adoptedUpdate" | "ownedUpdate";

/** An operation's allowlist. "all" (or absent) means every property is written. */
export type WriteMaskEntryDraft = "all" | string[];

export type IdentityResolveDraft =
  // A policy saved with no id source at all. Valid, honored by the runtime
  // ("never resolve", so every write takes the not-found answer), and only
  // authorable outside this screen — the editor shows it rather than quietly
  // presenting it as something else.
  | { strategy: "none" }
  | { strategy: "event-path"; path?: string }
  | {
      strategy: "lookup";
      instanceId?: string;
      view?: string;
      keyColumn?: string;
      valueColumn?: string;
      keyPath?: string;
      keyType?: "string" | "number";
    };

export interface DestinationIdentityPolicyDraft {
  resolve?: IdentityResolveDraft;
  idField?: string;
  onResolutionMiss?: NotFoundChoice;
  onResolvedRecordMissing?: NotFoundChoice;
  onWriteRace?: NotFoundChoice;
  writeMask?: Partial<Record<WriteOperation, WriteMaskEntryDraft>>;
}

export interface IdentityConnection {
  id: string;
  name: string;
  connectorType: string;
}

// ─── Operator copy ──────────────────────────────────────────────────────────
// Every operator-facing string lives here so the wording is reviewable in one
// place and never leaks a schema field name. Audience: CS and onboarding.

const QUESTION = {
  enable: "Write to a record that already exists?",
  strategy: "Where does the record's id come from?",
  path: "Which field on the event holds the id?",
  instanceId: "Which connection holds the mapping?",
  view: "Which table or view?",
  keyColumn: "Which column do we match on?",
  valueColumn: "Which column holds the id?",
  keyPath: "Which field on the event do we match with?",
  keyType: "Is the matching column a number?",
  idField: "Which property identifies the record?",
  onResolutionMiss: "When no matching record is found",
  onResolvedRecordMissing: "When the record we were pointed at is gone",
  onWriteRace: "When the record disappears while we are writing",
} as const;

const STRATEGY_LABEL: Record<IdentityResolveDraft["strategy"], string> = {
  "event-path": "A property on the incoming event",
  lookup: "A lookup in your data warehouse",
  none: "Nowhere — this pipeline never writes to an existing record (current)",
};

// Order matters, and so does which one is first: an operator who reads nothing
// and changes nothing must not end up creating records in a customer's CRM.
const NOT_FOUND_CHOICES: { value: NotFoundChoice; label: string }[] = [
  { value: "skip", label: "Skip this order" },
  { value: "fail", label: "Stop and raise an error" },
  { value: "create", label: "Create a new record" },
];

/** The safe answer every not-found moment starts on. Never `create`. */
const SAFE_CHOICE: NotFoundChoice = "skip";

const NOT_FOUND_MOMENTS = [
  { key: "onResolutionMiss", id: "adopt-miss" },
  { key: "onResolvedRecordMissing", id: "adopt-missing" },
  { key: "onWriteRace", id: "adopt-race" },
] as const;

const MASK_GROUPS: { op: WriteOperation; label: string }[] = [
  { op: "create", label: "Create a new record" },
  { op: "adoptedUpdate", label: "Update an adopted record" },
  { op: "ownedUpdate", label: "Update a record we made" },
];

/** Spec §6.5, shown literally so the screen and the runtime never disagree. */
const PRECEDENCE_LINE = "Field mapping → merge → mask → locked keys restored.";

/** Connector types whose instances can answer an identity lookup. Mirrors the
 *  `lookup` capability in the registry — the same shape `EnrichmentsField` uses
 *  to filter its own connection picker. */
const LOOKUP_CONNECTOR_TYPES = ["snowflake-source"];

// ─── Helpers ────────────────────────────────────────────────────────────────

/** "hubspot.deal" → "HubSpot deal". Singular on purpose: pluralising vendor
 *  object names ("companys") reads worse than the extra article. */
function destLabel(destObjectType: string | undefined): string {
  if (!destObjectType) return "record at the destination";
  const dot = destObjectType.indexOf(".");
  if (dot === -1) return destObjectType.replace(/_/g, " ");
  return `${vendorLabel(destObjectType.slice(0, dot))} ${destObjectType.slice(dot + 1).replace(/_/g, " ")}`;
}

function emptyDraft(identityFields: string[]): DestinationIdentityPolicyDraft {
  return {
    resolve: { strategy: "event-path", path: "" },
    idField: identityFields[0],
    onResolutionMiss: SAFE_CHOICE,
    onResolvedRecordMissing: SAFE_CHOICE,
    onWriteRace: SAFE_CHOICE,
  };
}

/** Turning adoption off on a pipeline whose policy is already SAVED (spec §14).
 *  The API refuses a stored policy being removed — the archive guard refuses to
 *  delete records this pipeline adopted only while the policy exists — so the
 *  switch sends the shape that means "adopt nothing new" instead of `null`, and
 *  the gesture keeps working. Filled to the schema's required fields so the
 *  resulting save is a valid policy however half-finished the draft was. */
function noneDraft(
  value: DestinationIdentityPolicyDraft | null,
  identityFields: string[],
): DestinationIdentityPolicyDraft {
  return {
    ...(value ?? {}),
    resolve: { strategy: "none" },
    idField: value?.idField ?? identityFields[0],
    onResolutionMiss: value?.onResolutionMiss ?? SAFE_CHOICE,
    onResolvedRecordMissing: value?.onResolvedRecordMissing ?? SAFE_CHOICE,
    onWriteRace: value?.onWriteRace ?? SAFE_CHOICE,
  };
}

/** The properties a mask may name — the unmaskable ones are written regardless,
 *  and the API rejects a mask that lists one. */
function writableIds(destProperties: { id: string }[], unmaskable: Set<string>): string[] {
  return destProperties.map((p) => p.id).filter((id) => !unmaskable.has(id));
}

/**
 * Drop unmaskable properties from every explicit allow-list in a policy.
 *
 * The save-time validator refuses a mask that names one, and the checkbox for
 * it is disabled — so a policy that already carries one has no gesture on this
 * screen that removes it, and every save 422s until someone edits the row by
 * hand. That cannot happen through the API today (the validator has refused
 * these since the feature landed), but it arms itself the day a transform
 * GAINS a system property: a new association key, or a source time added where
 * there was none. Existing masks naming that property become unsaveable.
 *
 * Applied where the form submits rather than as the operator types. A stale
 * entry sits in a group the operator may never touch, and `toggleMask` only
 * rebuilds the group whose checkbox was clicked — editing the pipeline name and
 * saving has to be enough to heal it.
 *
 * "all" is left alone: it names no properties, so it cannot name a bad one.
 * A list that empties out stays an empty list — "write only what is written
 * regardless" is a real instruction, and the validator accepts it.
 */
export function stripUnmaskable(
  policy: DestinationIdentityPolicyDraft | null,
  unmaskableKeys: string[],
): DestinationIdentityPolicyDraft | null {
  if (policy?.writeMask == null || unmaskableKeys.length === 0) return policy;
  const unmaskable = new Set(unmaskableKeys);
  let changed = false;
  const mask: Partial<Record<WriteOperation, WriteMaskEntryDraft>> = {};
  for (const [op, entry] of Object.entries(policy.writeMask) as [WriteOperation, WriteMaskEntryDraft][]) {
    if (entry === "all" || !Array.isArray(entry)) {
      mask[op] = entry;
      continue;
    }
    const kept = entry.filter((id) => !unmaskable.has(id));
    if (kept.length !== entry.length) changed = true;
    mask[op] = kept;
  }
  return changed ? { ...policy, writeMask: mask } : policy;
}

function isChecked(entry: WriteMaskEntryDraft | undefined, propId: string): boolean {
  if (entry === undefined || entry === "all") return true;
  return entry.includes(propId);
}

// ─── Component ──────────────────────────────────────────────────────────────

export function IdentityPolicyEditor({
  value,
  onChange,
  destObjectType,
  identityFields,
  connections,
  discover,
  destProperties,
  unmaskableKeys,
  savedPolicyExists = false,
}: {
  value: DestinationIdentityPolicyDraft | null;
  onChange: (v: DestinationIdentityPolicyDraft | null) => void;
  destObjectType?: string;
  identityFields: string[];
  connections: IdentityConnection[];
  discover: (instanceId: string, kind: string) => Promise<{ items: { id: string; name: string }[] }>;
  destProperties: { id: string; name: string }[];
  /** Properties a write mask may not name (spec §6.5) — the transform's locked
   *  keys PLUS its source-time property. This is deliberately not the same list
   *  a field mapping is checked against: a mapping cannot corrupt the
   *  source-time stamp, but two of the three deliver write paths apply the mask
   *  without re-stamping, so a mask really can drop it. Passing the shorter
   *  mapping list here is what made the screen offer a checkbox whose only
   *  effect was a 422 on save. */
  unmaskableKeys: string[];
  /** True ⇒ this pipeline's SAVED policy is non-null, so it may already have
   *  written to records the customer created. Turning adoption off then means
   *  "adopt nothing new", never "forget there was a policy" — the API refuses
   *  the latter (spec §14). False on a pipeline being created: nothing has been
   *  adopted, so the switch may go all the way back to `null`. */
  savedPolicyExists?: boolean;
}) {
  // A policy exists at all (saved or half-drafted on this screen)...
  const configured = value !== null;
  // ...and it points somewhere, which is what "adopting" means. A `none` policy
  // is configured but adopts nothing, so the switch reads it as off.
  const adopting = configured && value.resolve?.strategy !== "none";
  const target = destLabel(destObjectType);
  const unmaskable = new Set(unmaskableKeys);

  function toggleAdoption() {
    if (!adopting) {
      // Back on: keep everything the saved policy already says and give it an
      // id source again, rather than discarding the operator's answers.
      return onChange(
        configured
          ? { ...value, resolve: { strategy: "event-path", path: "" } }
          : emptyDraft(identityFields),
      );
    }
    onChange(savedPolicyExists ? noneDraft(value, identityFields) : null);
  }

  // Unmaskable properties always show, even when discovery didn't return them —
  // the point of the row is to tell the operator they can't be masked.
  const properties = [
    ...destProperties,
    ...unmaskableKeys.filter((k) => !destProperties.some((p) => p.id === k)).map((k) => ({ id: k, name: k })),
  ];
  const writable = writableIds(properties, unmaskable);
  const visible = new Set(properties.map((p) => p.id));

  /** Properties a saved mask names that this screen cannot show — discovery
   *  didn't return them. They are still the operator's instruction about a
   *  customer's record, so they are displayed and preserved, never dropped. */
  function carriedFor(op: WriteOperation): string[] {
    const entry = value?.writeMask?.[op];
    if (entry === undefined || entry === "all") return [];
    return entry.filter((id) => !visible.has(id));
  }

  function set(patch: Partial<DestinationIdentityPolicyDraft>) {
    onChange({ ...(value ?? {}), ...patch });
  }

  function setResolve(patch: Partial<Extract<IdentityResolveDraft, { strategy: "lookup" }>>) {
    const current = value?.resolve;
    if (current?.strategy !== "lookup") return;
    set({ resolve: { ...current, ...patch } });
  }

  function pickStrategy(strategy: IdentityResolveDraft["strategy"]) {
    // Reset rather than merge — the other strategy's fields mean nothing here,
    // and carrying them over would save a policy the operator never described.
    if (strategy === "none") return set({ resolve: { strategy } });
    set({ resolve: strategy === "event-path" ? { strategy, path: "" } : { strategy } });
  }

  function toggleMask(op: WriteOperation, propId: string, next: boolean) {
    const entry = value?.writeMask?.[op];
    const saved = entry === undefined || entry === "all" ? null : entry;
    const chosen = new Set(saved ?? writable);
    if (next) chosen.add(propId);
    else chosen.delete(propId);

    // Entries the screen cannot show survive the click. Rebuilding the mask from
    // the discovered list alone would drop them, and a mask that loses its last
    // named property stops masking — the pipeline would write every field on a
    // record the customer edits by hand.
    const offscreen = Array.from(chosen).filter((id) => !visible.has(id));
    const ordered = [...offscreen, ...writable.filter((id) => chosen.has(id))];

    const mask = { ...(value?.writeMask ?? {}) };
    // Everything writable ticked is exactly "all" — store nothing, so the saved
    // policy stays the smallest thing that describes the operator's intent. Only
    // when the screen shows the whole list, though: with an offscreen entry in
    // play, "absent" would mean more than the operator ever asked for.
    if (offscreen.length === 0 && ordered.length === writable.length) delete mask[op];
    else mask[op] = ordered;
    const empty = Object.keys(mask).length === 0;
    set({ writeMask: empty ? undefined : mask });
  }

  const resolve = value?.resolve;
  const lookup = resolve?.strategy === "lookup" ? resolve : null;
  // "none" is offered only while it is what the policy already says. Picking a
  // real source replaces it, and it never reappears as a fresh choice.
  const strategies: IdentityResolveDraft["strategy"][] =
    resolve?.strategy === "none" ? ["none", "event-path", "lookup"] : ["event-path", "lookup"];
  // A field saved earlier stays selected even when the connector no longer
  // declares it: showing the first declared field instead would report an
  // identity the policy does not use.
  const savedIdField = value?.idField;
  const idFieldOptions = [
    ...identityFields.map((f) => ({ value: f, label: f })),
    ...(savedIdField && !identityFields.includes(savedIdField)
      ? [{ value: savedIdField, label: `${savedIdField} (current)` }]
      : []),
  ];
  const lookupConnections = connections.filter((c) => LOOKUP_CONNECTOR_TYPES.includes(c.connectorType));
  const columnKind = lookup?.view ? `column:${lookup.view}` : "column";
  const anyCarried = MASK_GROUPS.some((g) => carriedFor(g.op).length > 0);

  return (
    <div className="adopt">
      <div className="adopt__switch">
        <label className="adopt__switch-label">
          <input
            type="checkbox"
            checked={adopting}
            onChange={toggleAdoption}
          />
          <span>{QUESTION.enable}</span>
        </label>
        <InfoTip termKey="adoptedRecord" />
      </div>

      {!configured && (
        <p className="adopt__off">Off. Every record this pipeline sends creates a new {target}.</p>
      )}
      {configured && !adopting && (
        // Why the settings are still on screen after turning it off. They are
        // what keeps Anansi from deleting records the customer created, and an
        // operator who cannot see that would read the section as stale.
        <p className="adopt__off">
          Off. Nothing new is adopted, and the settings below stay on record — that is what keeps
          Anansi from deleting the {target}s your customer created.
        </p>
      )}

      {configured && (
        <div className="adopt__body">
          <RouteLine resolve={resolve} idField={value?.idField} target={target} />

          <section className="adopt__group">
            <GroupHead title="WHERE THE ID COMES FROM" termKey="recordIdSource" />

            <Field id="adopt-strategy" label={QUESTION.strategy}>
              <select
                id="adopt-strategy"
                className="field__input"
                value={resolve?.strategy ?? "event-path"}
                onChange={(e) => pickStrategy(e.target.value as IdentityResolveDraft["strategy"])}
              >
                {strategies.map((s) => (
                  <option key={s} value={s}>{STRATEGY_LABEL[s]}</option>
                ))}
              </select>
            </Field>

            {resolve?.strategy === "none" ? (
              <p className="adopt__note">
                Nothing is looked up, so every write takes the “when no matching record is
                found” answer below. Choose a source above to change that.
              </p>
            ) : resolve?.strategy === "lookup" ? (
              <div className="adopt__lookup">
                <Field id="adopt-instance" label={QUESTION.instanceId}>
                  <select
                    id="adopt-instance"
                    className="field__input"
                    value={lookup?.instanceId ?? ""}
                    onChange={(e) => setResolve({ instanceId: e.target.value })}
                  >
                    <option value="">—</option>
                    {lookupConnections.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                    {/* A connection saved earlier stays selected even if it no
                        longer matches — losing it silently would rewrite the
                        policy on the next save. */}
                    {lookup?.instanceId && !lookupConnections.some((c) => c.id === lookup.instanceId) && (
                      <option value={lookup.instanceId}>{lookup.instanceId} (current)</option>
                    )}
                  </select>
                </Field>

                <Field id="adopt-view" label={QUESTION.view}>
                  <DiscoverInput
                    id="adopt-view"
                    ariaLabel={QUESTION.view}
                    instanceId={lookup?.instanceId}
                    discoverKind="view"
                    discover={discover}
                    value={lookup?.view ?? ""}
                    onChange={(v) => setResolve({ view: v })}
                  />
                </Field>

                <Field id="adopt-key-column" label={QUESTION.keyColumn}>
                  <DiscoverInput
                    id="adopt-key-column"
                    ariaLabel={QUESTION.keyColumn}
                    instanceId={lookup?.instanceId}
                    discoverKind={columnKind}
                    discover={discover}
                    value={lookup?.keyColumn ?? ""}
                    onChange={(v) => setResolve({ keyColumn: v })}
                  />
                </Field>

                <Field id="adopt-value-column" label={QUESTION.valueColumn}>
                  <DiscoverInput
                    id="adopt-value-column"
                    ariaLabel={QUESTION.valueColumn}
                    instanceId={lookup?.instanceId}
                    discoverKind={columnKind}
                    discover={discover}
                    value={lookup?.valueColumn ?? ""}
                    onChange={(v) => setResolve({ valueColumn: v })}
                  />
                </Field>

                <Field id="adopt-key-path" label={QUESTION.keyPath}>
                  <input
                    id="adopt-key-path"
                    className="field__input"
                    value={lookup?.keyPath ?? ""}
                    onChange={(e) => setResolve({ keyPath: e.target.value })}
                  />
                </Field>

                <Field id="adopt-key-type" label={QUESTION.keyType}>
                  <select
                    id="adopt-key-type"
                    className="field__input"
                    value={lookup?.keyType ?? "string"}
                    onChange={(e) => setResolve({ keyType: e.target.value === "number" ? "number" : "string" })}
                  >
                    <option value="string">No — text</option>
                    <option value="number">Yes — a number</option>
                  </select>
                </Field>
              </div>
            ) : (
              <Field id="adopt-path" label={QUESTION.path} hint="A field name from the order, e.g. dealId.">
                <input
                  id="adopt-path"
                  className="field__input"
                  value={resolve?.strategy === "event-path" ? resolve.path ?? "" : ""}
                  onChange={(e) => set({ resolve: { strategy: "event-path", path: e.target.value } })}
                />
              </Field>
            )}
          </section>

          <section className="adopt__group">
            <GroupHead title="THE RECORD WE MATCH" />
            {idFieldOptions.length === 0 ? (
              <>
                <label className="adopt__q-label" htmlFor="adopt-id-field">{QUESTION.idField}</label>
                <p className="adopt__note">
                  This destination doesn't offer a property to match on. Pick a different destination, or leave adoption off.
                </p>
              </>
            ) : (
              <Field id="adopt-id-field" label={QUESTION.idField} hint={`The property on the ${target} the id above lines up with.`}>
                <select
                  id="adopt-id-field"
                  className="field__input"
                  value={value?.idField ?? identityFields[0]}
                  onChange={(e) => set({ idField: e.target.value })}
                >
                  {idFieldOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>
            )}
          </section>

          <section className="adopt__group">
            <GroupHead title="WHEN THERE IS NO RECORD" />
            <p className="adopt__note">This pipeline only adds new records where you tell it to.</p>
            <div className="adopt__moments">
              {NOT_FOUND_MOMENTS.map((m) => (
                <Field key={m.key} id={m.id} label={QUESTION[m.key]}>
                  <select
                    id={m.id}
                    className="field__input"
                    value={value?.[m.key] ?? SAFE_CHOICE}
                    onChange={(e) => set({ [m.key]: e.target.value as NotFoundChoice })}
                  >
                    {NOT_FOUND_CHOICES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </Field>
              ))}
            </div>
          </section>

          <section className="adopt__group">
            <GroupHead title="WHAT WE WRITE" termKey="writeMask" />
            <p className="adopt__precedence">{PRECEDENCE_LINE}</p>
            {properties.length === 0 && !anyCarried ? (
              <p className="adopt__note">No properties to choose from yet — every property is written.</p>
            ) : (
              <>
                {properties.length === 0 && (
                  <p className="adopt__note">
                    Couldn't load this destination's properties. The saved list below is still in force.
                  </p>
                )}
                <div className="adopt__masks">
                  {MASK_GROUPS.map((g) => (
                    <div key={g.op} className="adopt__mask">
                      <div className="adopt__mask-head uppercase-label">{g.label}</div>
                      <div className="adopt__mask-list">
                        {properties.map((p) => {
                          const isUnmaskable = unmaskable.has(p.id);
                          return (
                            <label key={p.id} className={`adopt__prop${isUnmaskable ? " is-locked" : ""}`}>
                              <input
                                type="checkbox"
                                checked={isUnmaskable || isChecked(value?.writeMask?.[g.op], p.id)}
                                disabled={isUnmaskable}
                                onChange={(e) => toggleMask(g.op, p.id, e.target.checked)}
                              />
                              <span className="adopt__prop-name">{p.name}</span>
                              {isUnmaskable && <span className="adopt__prop-note">Always written.</span>}
                            </label>
                          );
                        })}
                        {/* An operation with nothing to list still says what it does. */}
                        {properties.length === 0 && carriedFor(g.op).length === 0 && (
                          <p className="adopt__mask-empty">Every property is written.</p>
                        )}
                        {/* Saved, enforced, and not in this destination's list. The row
                            says so rather than letting the mask look shorter than it is. */}
                        {carriedFor(g.op).map((id) => (
                          <label key={id} className="adopt__prop is-locked">
                            <input type="checkbox" checked readOnly disabled />
                            <span className="adopt__prop-name">{id}</span>
                            <span className="adopt__prop-note">Kept from the saved list.</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────────────

function GroupHead({ title, termKey }: { title: string; termKey?: "recordIdSource" | "writeMask" }) {
  return (
    <div className="adopt__group-head">
      <span className="uppercase-label">{title}</span>
      {termKey && <InfoTip termKey={termKey} />}
    </div>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="field">
      <label className="adopt__q-label" htmlFor={id}>{label}</label>
      {children}
      {hint && <div className="field__hint">{hint}</div>}
    </div>
  );
}

/** The configured route, drawn as nodes on a line: where the id is read, what it
 *  passes through, and the record it lands on. Fills in as the operator answers,
 *  so the whole policy reads back in one line. */
function RouteLine({
  resolve,
  idField,
  target,
}: {
  resolve: IdentityResolveDraft | undefined;
  idField: string | undefined;
  target: string;
}) {
  const nodes: { label: string; value: string }[] = [{ label: "Event", value: pathOf(resolve) }];
  if (resolve?.strategy === "lookup") {
    nodes.push({ label: "Warehouse", value: resolve.view || "—" });
  }
  nodes.push({ label: target, value: idField || "—" });

  return (
    <div className="adopt__route">
      {nodes.map((n, i) => (
        <Fragment key={`${n.label}-${i}`}>
          {i > 0 && <span className="adopt__route-link" aria-hidden="true" />}
          <span className="adopt__route-node">
            <span className="adopt__route-dot" aria-hidden="true" />
            <span className="adopt__route-label uppercase-label">{n.label}</span>
            <span className="adopt__route-value mono">{n.value}</span>
          </span>
        </Fragment>
      ))}
    </div>
  );
}

function pathOf(resolve: IdentityResolveDraft | undefined): string {
  if (resolve?.strategy === "none") return "not used";
  if (resolve?.strategy === "event-path") return resolve.path || "—";
  if (resolve?.strategy === "lookup") return resolve.keyPath || "—";
  return "—";
}
